import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Star, Loader2, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { APP } from "@/constants/testIds";

const CATEGORIES = [
  { value: "general", label: "General" },
  { value: "bug", label: "Bug report" },
  { value: "feature", label: "Feature request" },
  { value: "pricing", label: "Pricing / plans" },
];

export default function FeedbackDialog({ open, onOpenChange, defaultEmail }) {
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [email, setEmail] = useState(defaultEmail || "");
  const [category, setCategory] = useState("general");
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (open && defaultEmail && !email) setEmail(defaultEmail);
  }, [open, defaultEmail, email]);

  const submit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) {
      toast.error("Please add a comment before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        rating,
        comment: comment.trim(),
        email: email.trim() || null,
        category,
      });
      toast.success("Thanks for the feedback!");
      setComment("");
      setRating(5);
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong";
      toast.error(typeof msg === "string" ? msg : "Failed to send");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={APP.feedbackDialog}
        className="max-w-lg bg-[color:var(--bg)] text-[color:var(--fg)] border-2 border-[color:var(--fg)] rounded-none p-0 overflow-hidden"
      >
        <DialogHeader className="p-6 border-b border-[color:var(--fg)] text-left">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare size={14} />
            <span className="text-[10px] font-mono-tight uppercase tracking-widest">// feedback</span>
          </div>
          <DialogTitle className="font-display text-3xl leading-none tracking-tight">
            Tell us what you think.
          </DialogTitle>
          <DialogDescription className="text-sm text-[color:var(--muted)] mt-2">
            The app is free while we gather feedback. Every note reaches the founder directly.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="p-6 space-y-5">
          <div>
            <div className="text-[10px] font-mono-tight uppercase tracking-widest text-[color:var(--muted)] mb-2">Rating</div>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  type="button"
                  key={n}
                  data-testid={`${APP.feedbackStar}-${n}`}
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHoverRating(n)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1"
                  aria-label={`Rate ${n} out of 5`}
                >
                  <Star
                    size={28}
                    strokeWidth={1.5}
                    className={
                      n <= (hoverRating || rating)
                        ? "fill-[color:var(--yellow)] text-[color:var(--fg)]"
                        : "text-[color:var(--muted)]"
                    }
                  />
                </button>
              ))}
              <span className="ml-3 font-mono-tight text-sm">{rating}/5</span>
            </div>
          </div>

          <label className="block">
            <span className="text-[10px] font-mono-tight uppercase tracking-widest text-[color:var(--muted)]">Category</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1 w-full px-3 py-2.5 bg-transparent border border-[color:var(--fg)] focus:outline-none text-sm font-semibold"
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value} className="bg-[color:var(--bg)] text-[color:var(--fg)]">{c.label}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-[10px] font-mono-tight uppercase tracking-widest text-[color:var(--muted)]">
              What&apos;s working, what&apos;s not, what you wish existed?
            </span>
            <textarea
              data-testid={APP.feedbackComment}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={5}
              maxLength={2000}
              placeholder="Be honest — one line or an essay, both work."
              className="mt-1 w-full px-3 py-2.5 bg-transparent border border-[color:var(--fg)] focus:outline-none text-sm resize-none"
              required
            />
            <div className="text-[10px] font-mono-tight text-[color:var(--muted)] text-right mt-1">
              {comment.length} / 2000
            </div>
          </label>

          <label className="block">
            <span className="text-[10px] font-mono-tight uppercase tracking-widest text-[color:var(--muted)]">
              Email (optional — for follow-up)
            </span>
            <input
              data-testid={APP.feedbackEmail}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-1 w-full px-3 py-2.5 bg-transparent border border-[color:var(--fg)] focus:outline-none text-sm"
            />
          </label>

          <button
            type="submit"
            data-testid={APP.feedbackSubmit}
            disabled={submitting}
            className="w-full py-4 bg-[color:var(--fg)] text-[color:var(--bg)] font-semibold uppercase tracking-wider text-sm disabled:opacity-60 flex items-center justify-center gap-2 brutal-shadow"
          >
            {submitting ? (
              <><Loader2 size={16} className="animate-spin" /> Sending…</>
            ) : (
              <><Send size={14} /> Send feedback</>
            )}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
